import { AsyncPipe, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, Type } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import { map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';

import { PageType } from '@ppg/core/enums';
import { Page } from '@ppg/core/models';
import { createPendingTaskWatcher } from '@ppg/core/services';
import { LinkTagService, PageLevelMetaService } from '@ppg/shared/components';

import { PAGE_COMPONENT_IMPORT_MAP } from '../data/constants/page-components-map.const';

@Component({
  selector: 'ppg-page-renderer',
  standalone: true,
  imports: [AsyncPipe, NgComponentOutlet],
  providers: [PageLevelMetaService],
  templateUrl: './page-renderer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageRendererComponent implements OnInit {
  readonly page = input.required<Page>();

  readonly #metaService = inject(Meta);
  readonly #linkTagService = inject(LinkTagService);
  readonly #pageLevelMetaService = inject(PageLevelMetaService);

  readonly #pageComponentLoadTask = createPendingTaskWatcher();

  readonly pageComponent$ = toObservable(this.page).pipe(
    tap(() => this.#pageComponentLoadTask.startTask()),
    switchMap((page) =>
      this.#getComponentByPageType(page.type).pipe(
        map((component) => ({ component, inputs: { content: page.content } })),
      ),
    ),
    tap(() => this.#pageComponentLoadTask.stopTask()),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  ngOnInit(): void {
    const pageLevelMetaTags = this.#pageLevelMetaService.getMetaTags(this.page().meta, this.page().type);
    this.#metaService.addTags(pageLevelMetaTags);

    const pageLevelLinkTags = this.#pageLevelMetaService.getLinkTags(this.page().meta);
    this.#linkTagService.addOrUpdateTags(pageLevelLinkTags);
  }

  #getComponentByPageType(type: PageType): Observable<Type<unknown> | null> {
    return PAGE_COMPONENT_IMPORT_MAP[type] ?? of(null);
  }
}
